import React from 'react';
import { Link } from 'react-router-dom';
import {
  Download, ArrowRight, Quote as QuoteIcon, CheckCircle2,
  Building2, Zap, Trees, ShieldCheck,
} from 'lucide-react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { company } from '../mock';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PDF_URL = `${BACKEND_URL}/api/samples/case-studies.pdf`;

const caseStudies = [
  {
    persona: 'General Contractor',
    icon: Building2,
    title: 'Mid-Atlantic GC Wins a $14.2M Hospital TI on a 9-Day Bid Window',
    project: 'Healthcare interior fit-out · 64,000 RSF · Charlottesville, VA · Q3 2025',
    metrics: [
      { value: '$14.2M', label: 'Awarded Bid Value' },
      { value: '9 days', label: 'Total Bid Window' },
      { value: '48 hr', label: 'Core Turnaround' },
    ],
    facts: [
      ['Client', 'Holloway Commercial Builders (ENR Top-400 GC)'],
      ['Scope', '6 floors · OR suites, imaging, exam rooms'],
      ['Trades Priced', 'All 16 CSI divisions · GMP estimate'],
      ['Software Stack', 'Bluebeam · PlanSwift · Sage Estimating'],
    ],
    challenge:
      'Holloway received a healthcare RFP with a 9-day hard deadline while their two senior estimators were already booked on a $42M GMP and a $7M school addition. Walking away from the bid would have meant ceding a long-term hospital client to their biggest competitor.',
    approach: [
      'Day 1–2: Quantity takeoff for divisions 02, 03, 06, 07, 08, 09 — partition types mapped to assemblies, floor-by-floor rollups',
      'Day 3: MEP density study (CSI 21–28) with three sub quotes from Holloway\'s preferred trade partners',
      'Day 4–5: Pricing applied — RSMeans regional rates, fresh supplier quotes for medical-grade fixtures, prevailing-wage labor',
      'Day 6: Senior estimator QA pass + bid-review call. SOV, GMP letter, exclusions list, Bluebeam markups delivered',
      'Day 7–9: Holloway closed the bid. We remained on-call for two RFI responses',
    ],
    outcome:
      'Holloway submitted at $14,180,000 — second-lowest of six bidders but with the strongest technical proposal. Awarded the project citing "tighter scope definition and better risk allocation than the apparent low bidder." Final construction cost landed at $14,071,000 — within 0.8% of the estimate.',
    quote: {
      text: 'Without Core Estimation, we walk away from that bid. With them, we won it. Their numbers were tight, their backup was clean, and they were on call all the way through bid day.',
      who: 'Marcus Holloway',
      role: 'President, Holloway Commercial Builders',
    },
  },
  {
    persona: 'Electrical Subcontractor',
    icon: Zap,
    title: 'Northstar Electric Triples Bid Volume Without Hiring an Estimator',
    project: 'Commercial electrical contractor · $18M annual revenue · Northern Virginia · 2024–2026',
    metrics: [
      { value: '3.1×', label: 'Bid Volume Increase' },
      { value: '47%', label: 'Win Rate · up from 31%' },
      { value: '$4.8M', label: 'New Revenue · 12 months' },
    ],
    facts: [
      ['Pre-Core State', '14 bids/month · single in-house estimator'],
      ['Post-Core State', '44 bids/month · same in-house team'],
      ['Engagement', '24-month retainer — overflow + assemblies'],
      ['Labor Reference', 'NECA Manual of Labor Units'],
    ],
    challenge:
      'Northstar\'s single in-house estimator was capped at ~14 bids per month. They were leaving $3–5M in addressable pipeline on the table every year. Hiring a second senior electrical estimator would have cost $145K+ per year fully loaded with a 4–6 month ramp.',
    approach: [
      'Week 1–2: Rebuilt 22 of Northstar\'s most-used electrical assemblies inside Accubid, calibrated to their actual job-cost productivity and supplier pricing',
      'Standing capacity: 4 senior electrical estimators trained on Northstar\'s naming conventions, supplier list, and labor-rate sheets',
      'Workflow: bid invitation → Northstar tags it "Core" or "Internal" → Dropbox link to drawings → 24-hour turnaround standard, 48-hour complex',
      'QC: Northstar\'s manager spot-checks 1 in 4 deliverables — variance to internal pricing has averaged 1.6% over 24 months',
    ],
    outcome:
      'Bid volume rose from ~14/month to ~44/month in 6 months and has held steady. Win rate increased from 31% to 47% because the in-house team — freed from volume — spent more time on technical strategy for the larger bids. Net new revenue from Core\'s overflow exceeded $4.8M in year one. Cost to Northstar: ~$72K in fees — 67× ROI.',
    quote: {
      text: 'We thought we needed to hire a second estimator. We needed an extension of the team. Bid prep that used to take three days now takes one.',
      who: 'Priya Raman',
      role: 'Estimating Manager, Northstar Electric',
    },
  },
  {
    persona: 'Developer / Owner',
    icon: ShieldCheck,
    title: 'Cobalt Realty Catches $1.86M in Scope Gaps Before Signing a GMP',
    project: 'Multi-family developer · 184-unit garden apartment · Raleigh, NC · 2025',
    metrics: [
      { value: '$1.86M', label: 'Scope Gaps Found' },
      { value: '11.4%', label: 'of Original GMP' },
      { value: '65×', label: 'ROI on Our Fee' },
    ],
    facts: [
      ['Project', '184-unit Type V-A garden apartment'],
      ['GMP Submitted', '$16.3M (by selected GC)'],
      ['Engagement', 'Independent owner\'s estimator review'],
      ['Engagement Fee', '$28,500 · 0.17% of GMP'],
    ],
    challenge:
      'Cobalt selected a regional GC and received a $16.3M GMP for a 184-unit garden apartment build. Their internal team lacked bandwidth to do a line-by-line cost validation before equity partners required final signature.',
    approach: [
      'Day 1–4: Full re-takeoff of all 49 divisions from CD-set drawings. Quantities compared to GC\'s submitted SOV',
      'Day 5–8: Pricing reconciliation against current RSMeans, regional supplier quotes, and three reference projects of similar size',
      'Day 9–11: Scope-gap analysis — every item in the drawings/specs cross-referenced against the GC\'s SOV line items',
      'Day 12: Findings call with Cobalt and the GC',
      'Day 13–14: Final reconciled estimate + risk register delivered',
    ],
    findings: [
      ['Site Utilities (CSI 33)', '$248K', '$860K', '+$612K'],
      ['PT Slabs (CSI 03)', '$1.84M', '$2.05M', '+$214K'],
      ['Elevators (CSI 14)', '$420K', '$606K', '+$186K'],
      ['Fire Suppression (CSI 21)', '$310K', '$458K', '+$148K'],
      ['Other items (24 lines)', '—', '—', '+$700K'],
    ],
    outcome:
      '$1.18M absorbed into a revised GMP at no additional cost; $680K added to contingency with shared 50/50 risk language. Cobalt signed the GMP at $16.98M — still under their equity partners\' $17.4M ceiling, with substantially less hidden risk than the original offer. ROI on our $28,500 fee: 65×.',
    quote: {
      text: 'A 0.17% fee saved us nearly two million dollars and a year of change-order fights. Core Estimation is now part of our standard pre-GMP playbook on every project over $10M.',
      who: 'Elena Vasquez',
      role: 'VP Development, Cobalt Realty Partners',
    },
  },
];

export default function CaseStudies() {
  return (
    <>
      {/* Hero */}
      <section className="relative pt-32 sm:pt-40 pb-16 sm:pb-20 bg-[#0B1B33] overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)',
            backgroundSize: '48px 48px',
          }}
        />
        <div className="absolute -bottom-40 -right-32 w-[600px] h-[600px] bg-amber-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-6">
          <div className="text-[11px] sm:text-xs uppercase tracking-[0.2em] text-amber-400 mb-3 sm:mb-4">Case Studies</div>
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white tracking-tight leading-[1.08] max-w-3xl">
            Real wins. Real numbers. Real clients.
          </h1>
          <p className="mt-5 sm:mt-6 text-base sm:text-lg text-slate-300 max-w-2xl leading-relaxed">
            How Core Estimation LLC has helped contractors and developers win more bids, protect
            margins, and scale without hiring. Names lightly anonymized — methodology and outcomes are real.
          </p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3">
            <a href={PDF_URL} target="_blank" rel="noopener noreferrer">
              <Button className="bg-amber-500 hover:bg-amber-600 text-[#0B1B33] font-semibold h-11 px-5 rounded-xl w-full sm:w-auto">
                <Download className="w-4 h-4 mr-2" /> Download Full PDF
              </Button>
            </a>
            <Link to="/contact">
              <Button variant="outline" className="border-white/20 bg-white/5 text-white hover:bg-white/10 hover:text-amber-400 h-11 px-5 rounded-xl w-full sm:w-auto">
                Start Your Project <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Case studies */}
      <section className="py-12 sm:py-16 md:py-20 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 space-y-10 sm:space-y-12">
          {caseStudies.map((c, idx) => {
            const Icon = c.icon;
            return (
              <Card key={idx} className="bg-white rounded-2xl sm:rounded-3xl border-slate-100 overflow-hidden shadow-sm">
                {/* Card header */}
                <div className="bg-[#0B1B33] p-6 sm:p-8 md:p-10 text-white relative overflow-hidden">
                  <div className="absolute -top-20 -right-20 w-48 h-48 bg-amber-500/20 rounded-full blur-3xl pointer-events-none" />
                  <div className="relative flex flex-col gap-4">
                    <div className="flex flex-wrap items-center gap-3">
                      <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center flex-shrink-0">
                        <Icon className="w-5 h-5 text-[#0B1B33]" />
                      </div>
                      <Badge className="bg-white/10 text-amber-400 hover:bg-white/10 border border-amber-400/20 text-[11px]">
                        Case Study {String(idx + 1).padStart(2, '0')} · {c.persona}
                      </Badge>
                    </div>
                    <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold leading-tight tracking-tight">
                      {c.title}
                    </h2>
                    <p className="text-sm text-slate-300">{c.project}</p>
                  </div>
                </div>

                {/* Metric strip */}
                <div className="grid grid-cols-3 bg-[#06111F] text-white">
                  {c.metrics.map((m, i) => (
                    <div
                      key={i}
                      className={`p-4 sm:p-5 md:p-6 text-center ${
                        i > 0 ? 'border-l border-white/5' : ''
                      }`}
                    >
                      <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-amber-400 tracking-tight">
                        {m.value}
                      </div>
                      <div className="text-[10px] sm:text-xs text-slate-300 mt-1 uppercase tracking-wider leading-tight">
                        {m.label}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Body */}
                <div className="p-5 sm:p-8 md:p-10 space-y-7 sm:space-y-8">
                  {/* Quick facts */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 bg-slate-50 rounded-xl p-4 sm:p-5 border-l-4 border-amber-500">
                    {c.facts.map(([k, v], i) => (
                      <div key={i}>
                        <div className="text-[10px] sm:text-xs text-slate-500 uppercase tracking-wider">{k}</div>
                        <div className="text-sm font-semibold text-[#0B1B33] mt-0.5">{v}</div>
                      </div>
                    ))}
                  </div>

                  <div>
                    <h3 className="text-amber-600 text-xs uppercase tracking-wider font-semibold mb-2">The Challenge</h3>
                    <p className="text-slate-700 leading-relaxed text-[15px]">{c.challenge}</p>
                  </div>

                  <div>
                    <h3 className="text-amber-600 text-xs uppercase tracking-wider font-semibold mb-3">Our Approach</h3>
                    <ul className="space-y-2.5">
                      {c.approach.map((item, i) => (
                        <li key={i} className="flex items-start gap-3 text-[15px] text-slate-700 leading-relaxed">
                          <CheckCircle2 className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {c.findings && (
                    <div>
                      <h3 className="text-amber-600 text-xs uppercase tracking-wider font-semibold mb-3">Findings</h3>
                      <div className="overflow-x-auto rounded-xl border border-slate-200 -mx-1 sm:mx-0">
                        <table className="w-full text-xs sm:text-sm min-w-[420px]">
                          <thead className="bg-[#0B1B33] text-white">
                            <tr>
                              <th className="px-3 sm:px-4 py-2.5 text-left font-semibold uppercase tracking-wider text-[10px] sm:text-[11px]">Category</th>
                              <th className="px-3 sm:px-4 py-2.5 text-right font-semibold uppercase tracking-wider text-[10px] sm:text-[11px]">GC GMP</th>
                              <th className="px-3 sm:px-4 py-2.5 text-right font-semibold uppercase tracking-wider text-[10px] sm:text-[11px]">Core Review</th>
                              <th className="px-3 sm:px-4 py-2.5 text-right font-semibold uppercase tracking-wider text-[10px] sm:text-[11px]">Variance</th>
                            </tr>
                          </thead>
                          <tbody>
                            {c.findings.map((row, i) => (
                              <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50/60'}>
                                {row.map((cell, j) => (
                                  <td
                                    key={j}
                                    className={`px-3 sm:px-4 py-2.5 ${
                                      j === 0 ? 'text-slate-700 font-medium' :
                                      j === 3 ? 'text-right font-bold text-amber-600' :
                                      'text-right text-slate-600'
                                    }`}
                                  >
                                    {cell}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  <div>
                    <h3 className="text-amber-600 text-xs uppercase tracking-wider font-semibold mb-2">The Outcome</h3>
                    <p className="text-slate-700 leading-relaxed text-[15px]">{c.outcome}</p>
                  </div>

                  {/* Quote */}
                  <div className="relative bg-gradient-to-br from-amber-50 to-white border-l-4 border-amber-500 rounded-r-xl p-5 sm:p-6">
                    <QuoteIcon className="w-7 h-7 text-amber-500 mb-2" />
                    <p className="text-[#0B1B33] italic text-[15px] sm:text-base leading-relaxed">"{c.quote.text}"</p>
                    <div className="mt-4 text-sm">
                      <div className="font-bold text-[#0B1B33]">{c.quote.who}</div>
                      <div className="text-slate-500 text-xs">{c.quote.role}</div>
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-6">
          <div className="bg-[#0B1B33] rounded-2xl sm:rounded-3xl p-7 sm:p-10 md:p-14 grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white tracking-tight leading-tight">
                Ready to be our next case study?
              </h2>
              <p className="mt-3 sm:mt-4 text-slate-300 text-sm sm:text-base">
                Send us your drawings — we'll come back with scope, lead time and a fixed fee in 4 business hours.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 lg:justify-end">
              <Link to="/contact" className="w-full sm:w-auto">
                <Button size="lg" className="bg-amber-500 hover:bg-amber-600 text-[#0B1B33] font-semibold h-12 px-7 rounded-xl w-full">
                  Request a Quote <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <a href={`tel:${company.phoneRaw}`} className="w-full sm:w-auto">
                <Button size="lg" variant="outline" className="border-white/30 bg-transparent text-white hover:bg-white hover:text-[#0B1B33] h-12 px-7 rounded-xl w-full">
                  Call {company.phone}
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
